import { ref, computed } from 'vue';
import apiClient from '@/services/api/client';
import { Endpoints } from '@/config/Urls';
import type { User, TriviumLiteState } from '@/modules/admin/interfaces/User';

/**
 * XP-18 unlock — "Trivium Lite" interface.
 *
 * All truth lives on the server: lifetime XP, the unlock threshold, the
 * unlocked flag and the once-only announcement flag all arrive on the
 * /users/me payload. This composable only caches that state for the session
 * (module-level singleton) and reports dismissals back.
 */

const lifetimeXp = ref<number | null>(null);
const triviumLite = ref<TriviumLiteState | null>(null);
const loaded = ref(false);
let inflight: Promise<void> | null = null;

async function doFetch(): Promise<void> {
  try {
    const { data } = await apiClient.get<User>(Endpoints.ME);
    lifetimeXp.value = typeof data.lifetimeXp === 'number' ? data.lifetimeXp : null;
    triviumLite.value = data.triviumLite ?? null;
    loaded.value = true;
  } catch (err) {
    console.error('Failed to fetch interface unlock state:', err);
  } finally {
    inflight = null;
  }
}

export function useInterfaceUnlock() {
  /** Fetch once per session (deduplicated); pass force to refresh. */
  function fetchUnlockState(force = false): Promise<void> {
    if (loaded.value && !force) return Promise.resolve();
    if (!inflight) inflight = doFetch();
    return inflight;
  }

  /** Clear session cache (used on logout so the next user refetches). */
  function resetUnlockState(): void {
    lifetimeXp.value = null;
    triviumLite.value = null;
    loaded.value = false;
  }

  const unlocked = computed(() => triviumLite.value?.unlocked ?? false);
  const threshold = computed(() => triviumLite.value?.threshold ?? null);
  const announcementPending = computed(
    () => triviumLite.value?.announcementPending ?? false,
  );
  /** Lifetime points still needed to unlock (0 when unlocked or unknown). */
  const xpToUnlock = computed(() => {
    if (triviumLite.value == null || lifetimeXp.value == null) return null;
    return Math.max(0, triviumLite.value.threshold - lifetimeXp.value);
  });

  /**
   * Persist the dismissal server-side so the announcement never reappears on
   * a later login. Optimistically clears the local pending flag.
   */
  async function markAnnouncementSeen(): Promise<void> {
    if (triviumLite.value) {
      triviumLite.value = { ...triviumLite.value, announcementPending: false };
    }
    try {
      await apiClient.post(Endpoints.INTERFACE_UNLOCK_SEEN);
    } catch (err) {
      console.error('Failed to persist interface announcement dismissal:', err);
    }
  }

  return {
    lifetimeXp: computed(() => lifetimeXp.value),
    triviumLite: computed(() => triviumLite.value),
    loaded: computed(() => loaded.value),
    unlocked,
    threshold,
    announcementPending,
    xpToUnlock,
    fetchUnlockState,
    resetUnlockState,
    markAnnouncementSeen,
  };
}
